import {
  Box, Button, Card, CardContent, Chip, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useApiQuery, useApiMutation } from '@/hooks/useApiQuery';
import { useToast } from '@/components/ToastProvider';
import { practice } from '@/api/endpoints';
import { formatDateTime } from '@/utils/format';

type Review = {
  id: number;
  clinician_name: string;
  patient_initials: string;
  rating: number;
  body: string;
  moderation_status: 'pending' | 'approved' | 'rejected' | 'flagged';
  moderation_reason: string;
  appointment_id: string | null;
  created_at: string;
};

type TabKey = 'pending' | 'approved' | 'rejected_flagged';

export function PracticeReviewsPage() {
  const q = useApiQuery({
    queryKey: ['practice', 'reviews'],
    queryFn:  practice.reviews.list as () => Promise<Review[]>,
  });

  const buckets = useMemo(() => {
    const all = q.data ?? [];
    return {
      pending:           all.filter((r) => r.moderation_status === 'pending'),
      approved:          all.filter((r) => r.moderation_status === 'approved'),
      rejected_flagged:  all.filter((r) => r.moderation_status === 'rejected'
                                        || r.moderation_status === 'flagged'),
    };
  }, [q.data]);

  const [tab, setTab] = useState<TabKey>('pending');

  return (
    <>
      <PageHeader
        title="Reviews"
        subtitle="Moderate patient reviews. All reviews are anchored to a completed appointment."
      />

      <DataState query={q}>
        {() => (
          <>
            <Tabs
              value={tab} onChange={(_, v: TabKey) => setTab(v)}
              sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab value="pending"          label={`Pending (${buckets.pending.length})`} />
              <Tab value="approved"         label={`Approved (${buckets.approved.length})`} />
              <Tab value="rejected_flagged" label={`Rejected / flagged (${buckets.rejected_flagged.length})`} />
            </Tabs>
            {buckets[tab].length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {tab === 'pending' ? 'All caught up.' : 'Nothing here.'}
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {buckets[tab].map((r) => (
                  <ReviewRow key={r.id} review={r} interactive={tab === 'pending'} />
                ))}
              </Stack>
            )}
          </>
        )}
      </DataState>
    </>
  );
}

function ReviewRow({
  review, interactive,
}: { review: Review; interactive: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [flagOpen, setFlagOpen] = useState(false);

  const moderateMut = useApiMutation({
    mutationFn: (vars: { action: 'approve' | 'reject' | 'flag'; reason?: string }) =>
      practice.reviews.moderate(review.id, vars.action, vars.reason),
    onSuccess: () => {
      toast.success('Decision recorded.');
      qc.invalidateQueries({ queryKey: ['practice', 'reviews'] });
      setRejectOpen(false);
      setFlagOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <>
      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start"
                 spacing={2} sx={{ mb: 1.5 }}>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={0.25}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <StarIcon key={i}
                              sx={{ fontSize: 18,
                                    color: i < review.rating ? 'warning.main' : 'divider' }} />
                  ))}
                </Stack>
                <Chip size="small"
                      label={review.moderation_status}
                      variant="outlined"
                      color={
                        review.moderation_status === 'approved' ? 'success' :
                        review.moderation_status === 'pending' ? 'warning' :
                        'default'
                      } />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {review.clinician_name} · patient {review.patient_initials}
                <span className="tabular"> · {formatDateTime(review.created_at)}</span>
              </Typography>
            </Box>
          </Stack>

          {review.body && (
            <Box sx={{ p: 2, bgcolor: 'background.subtle', borderRadius: 1.5, mb: 1.5 }}>
              <Typography variant="body2">{review.body}</Typography>
            </Box>
          )}

          {review.moderation_reason && (
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mb: 1.5 }}>
              Moderator note: {review.moderation_reason}
            </Typography>
          )}

          {interactive && (
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="contained" color="success"
                      startIcon={<CheckIcon />}
                      onClick={() => moderateMut.mutate({ action: 'approve' })}
                      disabled={moderateMut.isPending}>
                Approve
              </Button>
              <Button size="small" variant="outlined" color="error"
                      startIcon={<CloseIcon />}
                      onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
              <Button size="small" variant="outlined" color="warning"
                      startIcon={<FlagIcon />}
                      onClick={() => setFlagOpen(true)}>
                Flag for legal
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={rejectOpen}
        title="Reject review?"
        description="The review is hidden from the public profile. The patient is notified with the reason."
        destructive reasonRequired confirmLabel="Reject"
        onCancel={() => setRejectOpen(false)}
        onConfirm={(reason) => moderateMut.mutateAsync({ action: 'reject', reason: reason ?? '' })}
      />
      <ConfirmDialog
        open={flagOpen}
        title="Flag for legal review?"
        description="The review is hidden until the legal team reviews it. Use this for content that may breach platform policy or law."
        reasonRequired confirmLabel="Flag"
        onCancel={() => setFlagOpen(false)}
        onConfirm={(reason) => moderateMut.mutateAsync({ action: 'flag', reason: reason ?? '' })}
      />
    </>
  );
}
